import {
  bedrockModelsExtract,
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  withAllowedModelsHint,
} from "../models";
import { customAuth } from "../standardAuthPatterns";
import { defineProduct, type ListFieldFetch, productVariant } from "../types";
import { domainToRegex, widenUrlPattern } from "../urlPatternConversion";
import logo from "./logo.svg";
import { awsBedrockUsage } from "./usage";

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const familyUrlPattern = (region: string) =>
  `https://bedrock(-[a-z-]+)?\\.${escapeRegex(region)}\\.amazonaws\\.com/.*`;

const bedrockUrlPattern = ({ region, endpointUrl }: Record<string, string>) => {
  const trimmedRegion = region?.trim();
  const family = trimmedRegion
    ? familyUrlPattern(trimmedRegion)
    : "https://bedrock(-[a-z-]+)?\\..*\\.amazonaws\\.com/.*";
  const endpoint = endpointUrl?.trim();
  return endpoint ? widenUrlPattern(family, domainToRegex(endpoint)) : family;
};

const bedrockTestUrl = ({ region }: Record<string, string>) =>
  `https://bedrock.${region?.trim() || "us-east-1"}.amazonaws.com/foundation-models`;

const bedrockModelsFetch: ListFieldFetch = {
  label: "Fetch models from Bedrock",
  request: bedrockTestUrl,
  extract: bedrockModelsExtract,
};

const bedrockEnvVars = (
  { region, endpointUrl }: Record<string, string>,
  { sessionToken }: { sessionToken: boolean }
): Record<string, string> => {
  const env: Record<string, string> = {
    AWS_ACCESS_KEY_ID: "{will-be-injected}",
    AWS_SECRET_ACCESS_KEY: "{will-be-injected}",
    AWS_REGION: region?.trim() || "us-east-1",
  };
  if (sessionToken) env.AWS_SESSION_TOKEN = "{will-be-injected}";
  const endpoint = endpointUrl?.trim();
  if (endpoint) env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME = endpoint;
  return env;
};

const regionHelperText =
  "Bedrock is regional — this connector is scoped to Bedrock in the region you enter (bedrock.<region>.amazonaws.com and bedrock-runtime.<region>.amazonaws.com).";

const vpcEndpointMarkdown =
  "If you reach Bedrock over [AWS PrivateLink](https://docs.aws.amazon.com/bedrock/latest/userguide/usingVPC.html), enter your interface VPC endpoint URL below. Requests are sent there instead of the public Bedrock runtime endpoint.\n\nLeave blank to use the public `bedrock-runtime.<region>.amazonaws.com` endpoint.";

const vpcEndpointPlaceholder =
  "https://vpce-0abc123-def456.bedrock-runtime.us-east-1.vpce.amazonaws.com";

const vpcEndpointHelperText =
  "Your PrivateLink interface endpoint for the Bedrock runtime.";

export const awsBedrock = defineProduct({
  name: "AWS Bedrock",
  tagLine: "Amazon Bedrock model inference",
  logo,
  tags: ["featured", "ai", "engineering"],
  variants: [
    productVariant({
      identifier: "awsBedrock",
      authLabel: "Access key",
      protocol: "http",
      defaultUrlPattern: bedrockUrlPattern,
      auth: (app) =>
        customAuth("awsSigV4", {
          accessKeyId: app.config.accessKeyId,
          secretAccessKey: app.secret.secretAccessKey,
          sessionToken: { from: "secret", field: "sessionToken" },
          region: app.config.region,
        }),
      steps: [
        {
          title: "Create an IAM user with Bedrock access",
          items: [
            {
              kind: "markdown",
              content:
                "Visit the [IAM Console](https://console.aws.amazon.com/iam/home#/users) and create a new user with programmatic access, or use an existing IAM user.\n\nAttach a policy granting access to Amazon Bedrock — for example `AmazonBedrockFullAccess`, or a scoped policy allowing `bedrock:InvokeModel`, `bedrock:InvokeModelWithResponseStream`, and `bedrock:ListFoundationModels`.",
            },
          ],
        },
        {
          title: "Generate access keys",
          items: [
            {
              kind: "markdown",
              content:
                "In the IAM user’s **Security credentials** tab, create a new access key. Choose **Application running outside AWS** as the use case.\n\nCopy the **Access key ID** and **Secret access key** — you’ll need them in the next step.",
            },
          ],
        },
        {
          title: "Enter your Bedrock credentials",
          items: [
            {
              kind: "input",
              label: "Access key ID",
              placeholder: "AKIA...",
              required: true,
              target: { kind: "configuration", field: "accessKeyId" },
            },
            {
              kind: "input",
              label: "Secret access key",
              placeholder: "Paste your secret access key",
              required: true,
              target: { kind: "secrets", field: "secretAccessKey" },
            },
            {
              kind: "input",
              label: "Region",
              placeholder: "us-east-1",
              required: true,
              helperText: regionHelperText,
              target: { kind: "configuration", field: "region" },
            },
          ],
        },
        {
          title: "Use a VPC endpoint (optional)",
          items: [
            { kind: "markdown", content: vpcEndpointMarkdown },
            {
              kind: "url",
              label: "VPC endpoint URL (optional)",
              placeholder: vpcEndpointPlaceholder,
              helperText: vpcEndpointHelperText,
              target: { kind: "configuration", field: "endpointUrl" },
            },
          ],
        },
        {
          title: "Choose available models (optional)",
          items: [
            {
              kind: "list",
              label: "Available models",
              prefix: MODELS_PREFIX,
              columns: [
                {
                  field: "name",
                  placeholder: "anthropic.claude-3-5-sonnet-20241022-v2:0",
                },
              ],
              addLabel: "Add model",
              fetch: bedrockModelsFetch,
              helperText: MODELS_HELPER_TEXT,
            },
          ],
        },
      ],
      connectionTest: { protocol: "http", request: bedrockTestUrl },
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "Call Amazon Bedrock with the AWS SDK or CLI as usual — list foundation models or inference profiles on the control plane (bedrock), then invoke on the runtime (bedrock-runtime). Pass a placeholder access key and secret; the proxy strips the local signature and re-signs each Bedrock request with the connected AWS credentials. Use process.env.AWS_REGION for the region, and process.env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME as the endpoint when it is set. Only Bedrock calls in this region are signed by this connector — other AWS services are not.",
          configuration
        ),
      publicEnvVars: (configuration) =>
        bedrockEnvVars(configuration, { sessionToken: false }),
      usage: awsBedrockUsage(),
    }),
    productVariant({
      identifier: "awsBedrockRole",
      authLabel: "IAM role (OIDC)",
      protocol: "http",
      defaultUrlPattern: bedrockUrlPattern,
      auth: customAuth("awsRole"),
      steps: [
        {
          title: "Set up an OIDC trust",
          items: [
            {
              kind: "markdown",
              content:
                "Register 3B as an OpenID Connect identity provider in your AWS account:\n\n1. Open the [IAM identity providers page](https://console.aws.amazon.com/iam/home#/identity_providers)\n2. Choose **Add provider** → **OpenID Connect**\n3. For **Provider URL**, enter the URL shown below\n4. For **Audience**, enter the value shown below\n5. Click **Add provider**",
            },
            {
              kind: "copyable",
              label: "Provider URL",
              value: "{OIDC_PROVIDER_URL}",
            },
            {
              kind: "copyable",
              label: "Audience",
              value: "connected-app:{CONNECTED_APP_ID}",
            },
          ],
        },
        {
          title: "Create an IAM role",
          items: [
            {
              kind: "markdown",
              content:
                "Create a role that trusts the 3B OIDC provider:\n\n1. Open the [IAM roles page](https://console.aws.amazon.com/iam/home#/roles) and click **Create role**\n2. Choose **Web identity**, select the 3B provider you just added, and select the audience shown above\n3. Attach a policy granting access to Amazon Bedrock — for example `AmazonBedrockFullAccess`, or a scoped policy allowing `bedrock:InvokeModel`, `bedrock:InvokeModelWithResponseStream`, and `bedrock:ListFoundationModels`\n4. Name the role and create it\n5. Copy the **Role ARN** from the role summary",
            },
          ],
        },
        {
          title: "Enter the role details",
          items: [
            {
              kind: "input",
              label: "Role ARN",
              placeholder: "arn:aws:iam::123456789012:role/My3BRole",
              required: true,
              target: { kind: "configuration", field: "roleArn" },
            },
            {
              kind: "input",
              label: "Region",
              placeholder: "us-east-1",
              required: true,
              helperText: regionHelperText,
              target: { kind: "configuration", field: "region" },
            },
          ],
        },
        {
          title: "Use a VPC endpoint (optional)",
          items: [
            { kind: "markdown", content: vpcEndpointMarkdown },
            {
              kind: "url",
              label: "VPC endpoint URL (optional)",
              placeholder: vpcEndpointPlaceholder,
              helperText: vpcEndpointHelperText,
              target: { kind: "configuration", field: "endpointUrl" },
            },
          ],
        },
        {
          title: "Choose available models (optional)",
          items: [
            {
              kind: "list",
              label: "Available models",
              prefix: MODELS_PREFIX,
              columns: [
                {
                  field: "name",
                  placeholder: "anthropic.claude-3-5-sonnet-20241022-v2:0",
                },
              ],
              addLabel: "Add model",
              fetch: bedrockModelsFetch,
              helperText: MODELS_HELPER_TEXT,
            },
          ],
        },
      ],
      connectionTest: { protocol: "http", request: bedrockTestUrl },
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "The AWS_* env var values are placeholder strings, not real credentials — leave them in place so the AWS SDK and aws CLI pick them up and attempt to sign, but don’t branch on or print them. The proxy intercepts every outbound Bedrock call (control plane and runtime), strips the local signature, and re-signs with short-lived AssumeRoleWithWebIdentity credentials. Use process.env.AWS_REGION for the region, and process.env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME as the endpoint when it is set. Only Bedrock calls in this region are signed by this connector — other AWS services are not.",
          configuration
        ),
      publicEnvVars: (configuration) =>
        bedrockEnvVars(configuration, { sessionToken: true }),
      usage: awsBedrockUsage(),
    }),
  ],
});
